import React, { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { useToast } from "../../../Components/Toast";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import { roConfigErrorMessage, useRoConfig } from "../../../Hooks/useRoConfig";
import { RoDimensionItem, RoWeightItem } from "../../../Types/RoConfig";

type Row = RoDimensionItem & { source: "Master"; weightPct: number };

const SourceBadge: React.FC = () => (
  <span className="inline-flex items-center rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-600">
    Master
  </span>
);

const DimensionsTable: React.FC<{
  title: string;
  rows: Row[];
  saving: boolean;
  onToggleIncluded: (key: string) => void;
}> = ({ title, rows, saving, onToggleIncluded }) => (
  <div className="mb-6 last:mb-0">
    <div className="mb-2 flex items-center justify-between gap-2">
      <p className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </p>
    </div>
    <div className="overflow-hidden rounded-xl border border-gray-200">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="border-b border-gray-200 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Key
            </th>
            <th className="border-b border-gray-200 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Label
            </th>
            <th className="border-b border-gray-200 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Source
            </th>
            <th className="border-b border-gray-200 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Included
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr
              key={row.key}
              className="border-b border-gray-100 last:border-b-0"
            >
              <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                {row.key}
              </td>
              <td className="px-4 py-2.5 font-medium text-gray-900">
                {row.label}
              </td>
              <td className="px-4 py-2.5">
                <SourceBadge />
              </td>
              <td className="px-4 py-2.5">
                <ToggleSwitch
                  id={`ro-dim-${row.key}`}
                  checked={row.included}
                  disabled={saving}
                  onChange={() => onToggleIncluded(row.key)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

function toRows(dims: RoDimensionItem[], weights: RoWeightItem[]): Row[] {
  const weightByKey = new Map(weights.map(w => [w.key, w.weightPct]));
  return dims.map(d => ({
    ...d,
    source: "Master" as const,
    weightPct: weightByKey.get(d.key) ?? 0,
  }));
}

const RoDimensionsPanel: React.FC = () => {
  const toast = useToast();
  const {
    riskDimensions,
    opportunityDimensions,
    dimensionWeights,
    loading,
    error,
    saveConfig,
  } = useRoConfig();

  const [riskRows, setRiskRows] = useState<Row[]>([]);
  const [opportunityRows, setOpportunityRows] = useState<Row[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading) {
      setRiskRows(
        toRows(
          riskDimensions,
          dimensionWeights.filter(w => w.category === "risk")
        )
      );
      setOpportunityRows(
        toRows(
          opportunityDimensions,
          dimensionWeights.filter(w => w.category === "opportunity")
        )
      );
      setHydrated(true);
    }
  }, [loading, riskDimensions, opportunityDimensions, dimensionWeights]);

  const persistDimensions = async (risk: Row[], opportunity: Row[]) => {
    setSaving(true);
    try {
      await saveConfig({
        dimensions: [...risk, ...opportunity].map(d => ({
          key: d.key,
          category: d.category,
          included: d.included,
          weight_pct: d.weightPct,
        })),
        metrics: [],
      });
      toast.success("R&O dimensions saved.");
    } catch (e: unknown) {
      toast.error(roConfigErrorMessage(e, "Failed to save R&O dimensions."));
      setRiskRows(
        toRows(
          riskDimensions,
          dimensionWeights.filter(w => w.category === "risk")
        )
      );
      setOpportunityRows(
        toRows(
          opportunityDimensions,
          dimensionWeights.filter(w => w.category === "opportunity")
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleIncluded = (category: "risk" | "opportunity", key: string) => {
    const apply = (prev: Row[]) =>
      prev.map(r => (r.key === key ? { ...r, included: !r.included } : r));
    if (category === "risk") {
      setRiskRows(prev => {
        const next = apply(prev);
        void persistDimensions(next, opportunityRows);
        return next;
      });
    } else {
      setOpportunityRows(prev => {
        const next = apply(prev);
        void persistDimensions(riskRows, next);
        return next;
      });
    }
  };

  if (loading || !hydrated) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading R&amp;O dimensions…
      </div>
    );
  }

  if (error) {
    return <p className="py-8 text-sm text-red-600">{error}</p>;
  }

  return (
    <div>
      {saving && (
        <div className="mb-3 flex items-center gap-2 text-xs text-gray-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Saving…
        </div>
      )}
      <DimensionsTable
        title="Risk dimensions"
        rows={riskRows}
        saving={saving}
        onToggleIncluded={key => toggleIncluded("risk", key)}
      />
      <DimensionsTable
        title="Opportunity dimensions"
        rows={opportunityRows}
        saving={saving}
        onToggleIncluded={key => toggleIncluded("opportunity", key)}
      />
    </div>
  );
};

export default RoDimensionsPanel;
